import { useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { FilterToolbarItemId } from 'src/components/Filters';
import { useInternalNotesFilter } from 'src/components/Filters/InternalNotesFilter';
import { useMergedTagFilters } from 'src/components/MainFilterBar/Tags/useMergedTagFilters';
import { usePurchaseInfoFilters } from 'src/components/MainFilterBar/usePurchaseInfoFilters';
import { Content } from 'src/contexts/ContentContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { DateRangeSelector } from 'src/core/POS/DateRangeSelector';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { Slider } from 'src/core/ui/Slider';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { PurchasePaymentMethodSelectionInput } from 'src/modals/common';
import { ContentId } from 'src/utils/constants/contentId';
import {
  PAYMENT_METHOD_TYPE_TO_CID,
  PURCHASE_PAYMENT_STATUS_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { StandardDateRangePresetNames } from 'src/utils/dateTimeUtils';
import { formatNumber } from 'src/utils/numberFormatter';
import {
  ActionOutboxEntityType,
  Feature,
  PurchaseOrderQuery,
  PurchaseOrderState,
} from 'src/WebApiController';

import { useDefaultYesNoFilterValues } from '../Filters/hooks/useDefaultYesNoFilterValues';
import {
  CurrencyFilterMultiSelector,
  CurrencyFilterSelector,
} from '../Selectors/CurrencyFilterSelector';
import { useDeliveryTypeFilters } from './Filters/useDeliveryTypeFilters';
import { usePurchaseIsConsignmentFilters } from './Filters/usePurchaseIsConsignmentFilters';
import {
  CommonEventFiltersProps,
  useCommonEventFilters,
} from './useCommonEventFilters';

export const purchaseMandatoryFiltersToShow: FilterToolbarItemId[] = [
  'purchaseDates',
];

interface PurchaseFiltersParams {
  isHorizontalLayout?: boolean;
  showCommissionerFilter?: boolean;
  showConsignmentFilter?: boolean;
  showCatalogFilters?: CommonEventFiltersProps['showCatalogFilters'];
  showVenueFilter?: CommonEventFiltersProps['showVenueFilter'];
  showPerformerFilter?: CommonEventFiltersProps['showPerformerFilter'];
}

export const usePurchaseFilters = ({
  isHorizontalLayout,
  showCommissionerFilter,
  showConsignmentFilter,
  showCatalogFilters,
  showVenueFilter,
  showPerformerFilter,
}: PurchaseFiltersParams) => {
  const hasFilterByCurrencyCodeMultiFeature = useUserHasFeature(
    Feature.FilterByCurrencyCodeMulti
  );
  const hasFilterByCurrencyCodeFeature = !hasFilterByCurrencyCodeMultiFeature;
  const hasFilterByPaymentCurrencyCodeMultiFeature = useUserHasFeature(
    Feature.FilterByPaymentCurrencyCodeMulti
  );

  const internalNotesFilter = useInternalNotesFilter<PurchaseOrderQuery>({
    entityType: ActionOutboxEntityType.Purchase,
    isHorizontalLayout,
  });

  const { initialQuery, tempQuery, setTempQuery } =
    useFilterQueryContext<PurchaseOrderQuery>();
  const { shouldEnableEmptySelection } = useDefaultYesNoFilterValues({});

  const isConsignmentFilter = usePurchaseIsConsignmentFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    enableEmptySelection: shouldEnableEmptySelection('isConsignment'),
  });

  const reportType = undefined;
  const purchaseInfoFilters = usePurchaseInfoFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    reportType,
    useRelativePresets: reportType != null,
    showCommissionerFilter,
  });

  const tagsFilter = useMergedTagFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    entityType: ActionOutboxEntityType.Purchase,
    includeEventTags: false,
  });

  const eventFilter = useCommonEventFilters({
    showCatalogFilters,
    showVenueFilter,
    showPerformerFilter,
  });

  const deliveryTypeFilter = useDeliveryTypeFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    allowCustomDeliveryType: true,
  });

  return useMemo(() => {
    const costHandle: [number, number] = [
      tempQuery.totalCostLow || 0,
      tempQuery.totalCostHigh || 50000,
    ];

    return [
      {
        titleContentId: ContentId.WhenWhereWho,
        type: 'row',
        items: [
          ...purchaseInfoFilters.items,
          ...(showConsignmentFilter ? [isConsignmentFilter] : []),
        ],
      },
      {
        titleContentId: ContentId.Payments,
        type: 'row',
        items: [
          {
            filterId: 'paymentDates' as FilterToolbarItemId,
            labelContentId: ContentId.PaymentDate,
            filterQueryKeys: ['paymentDates'] as FilterToolbarItemId[],
            filterItem: (
              <DateRangeSelector
                useRelativePresets
                presetNames={StandardDateRangePresetNames}
                value={tempQuery.paymentDates}
                defaultValue={initialQuery.paymentDates}
                onBlur={({ value }) =>
                  setTempQuery({
                    ...tempQuery,
                    paymentDates: value,
                  })
                }
              />
            ),
          },
          {
            filterId: 'paymentStatus' as FilterToolbarItemId,
            labelContentId: ContentId.PaymentState,
            filterQueryKeys: ['paymentStatus'] as FilterToolbarItemId[],
            filterItem: (
              <PosEnumSelect
                style={{ width: '100%' }}
                value={tempQuery.paymentStatus}
                placeholderText={ContentId.AllPaymentStates}
                enableEmptySelection
                onChange={(paymentStatus) => {
                  if (paymentStatus !== tempQuery.paymentStatus) {
                    setTempQuery({
                      ...tempQuery,
                      paymentStatus: paymentStatus,
                    });
                  }
                }}
                valueOptionsContent={PURCHASE_PAYMENT_STATUS_TO_CID}
              />
            ),
          },
          {
            filterId: 'paymentMethod' as FilterToolbarItemId,
            labelContentId: ContentId.PaymentType,
            filterQueryKeys: ['paymentMethod'] as FilterToolbarItemId[],
            filterItem: (
              <PosEnumSelect
                style={{ width: '100%' }}
                value={tempQuery.paymentMethod}
                placeholderText={ContentId.AllPaymentTypes}
                enableEmptySelection
                onChange={(paymentMethod) => {
                  if (paymentMethod !== tempQuery.paymentMethod) {
                    setTempQuery({
                      ...tempQuery,
                      paymentMethod: paymentMethod,
                    });
                  }
                }}
                valueOptionsContent={PAYMENT_METHOD_TYPE_TO_CID}
              />
            ),
          },
          ...(hasFilterByPaymentCurrencyCodeMultiFeature
            ? [
                {
                  filterId: 'paymentCurrencyCodes' as FilterToolbarItemId,
                  labelContentId: ContentId.PaymentCurrency,
                  filterQueryKeys: [
                    'paymentCurrencyCodes',
                  ] as FilterToolbarItemId[],
                  filterItem: (
                    <CurrencyFilterMultiSelector
                      values={tempQuery.paymentCurrencyCodes ?? []}
                      triggerProps={{ style: { width: '100%' } }}
                      placeholderText={ContentId.All}
                      enableEmptySelection
                      onChange={({ newValues: newPaymentCurrencyCodes }) => {
                        if (
                          !isEqual(
                            tempQuery.paymentCurrencyCodes,
                            newPaymentCurrencyCodes
                          )
                        ) {
                          setTempQuery({
                            ...tempQuery,
                            paymentCurrencyCodes:
                              newPaymentCurrencyCodes?.length
                                ? newPaymentCurrencyCodes
                                : null,
                          });
                        }
                      }}
                    />
                  ),
                },
              ]
            : []),
          {
            filterId: 'paymentMethodId' as FilterToolbarItemId,
            labelContentId: ContentId.PaymentMethodUsed,
            filterQueryKeys: ['paymentMethodId'] as FilterToolbarItemId[],
            filterItem: (
              <PurchasePaymentMethodSelectionInput
                style={{ width: '100%' }}
                value={tempQuery.paymentMethodId?.toString()}
                placeholderText={ContentId.AllPaymentMethods}
                enableEmptySelection
                hideAdditionalOptions
                onChange={(paymentMethodId) => {
                  if (
                    paymentMethodId !== tempQuery.paymentMethodId?.toString()
                  ) {
                    setTempQuery({
                      ...tempQuery,
                      paymentMethodId: paymentMethodId?.length
                        ? parseInt(paymentMethodId)
                        : null,
                    });
                  }
                }}
              />
            ),
          },
        ],
      },
      eventFilter,
      {
        titleContentId: ContentId.Delivery,
        type: 'row',
        items: [deliveryTypeFilter],
      },
      {
        titleContentId: ContentId.Status,
        type: 'row',
        items: [
          {
            filterId: 'purchaseOrderState' as FilterToolbarItemId,
            labelContentId: ContentId.ShowVoidCancelledPurchases,
            filterQueryKeys: ['purchaseOrderState'] as FilterToolbarItemId[],
            filterItem: (
              <Checkbox
                checked={
                  tempQuery.purchaseOrderState !== PurchaseOrderState.Active
                }
                onChange={(e) => {
                  const showCancelledAndVoided = e.target.checked;
                  const newPurchaseOrderState = showCancelledAndVoided
                    ? null
                    : PurchaseOrderState.Active;

                  if (newPurchaseOrderState !== tempQuery.purchaseOrderState) {
                    setTempQuery({
                      ...tempQuery,
                      purchaseOrderState: newPurchaseOrderState,
                    });
                  }
                }}
                labelPosition="right"
                label={
                  isHorizontalLayout ? undefined : (
                    <Content id={ContentId.ShowVoidCancelledPurchases} />
                  )
                }
              />
            ),
          },
        ],
      },
      {
        titleContentId: ContentId.Cost,
        items: [
          {
            filterId: 'totalCostHigh' as FilterToolbarItemId,
            labelContentId: ContentId.TotalCost,
            filterQueryKeys: [
              'totalCostLow',
              'totalCostHigh',
            ] as FilterToolbarItemId[],
            filterItem: (
              <Slider.Root
                min={0}
                max={50000}
                step={1}
                value={costHandle}
                renderHandle={(n) => (
                  <Slider.Handle>{formatNumber(n)}</Slider.Handle>
                )}
                onValueChange={([low, high]: [number, number]) => {
                  if (
                    low !== (tempQuery.totalCostLow || 0) ||
                    high !== tempQuery.totalCostHigh
                  ) {
                    const newQuery = {
                      ...tempQuery,
                      totalCostLow: low === 0 ? null : low,
                      totalCostHigh: high === 50000 ? null : high,
                    };
                    setTempQuery(newQuery);
                  }
                }}
              />
            ),
          },
          ...(hasFilterByCurrencyCodeFeature
            ? [
                {
                  filterId: 'currencyCode' as FilterToolbarItemId,
                  labelContentId: ContentId.Currency,
                  filterQueryKeys: ['currencyCode'] as FilterToolbarItemId[],
                  filterItem: (
                    <CurrencyFilterSelector
                      value={tempQuery.currencyCode ?? ''}
                      style={{ width: '100%' }}
                      placeholderText={ContentId.All}
                      onChange={(newCurrencyCode) => {
                        if (tempQuery.currencyCode !== newCurrencyCode) {
                          setTempQuery({
                            ...tempQuery,
                            currencyCode: newCurrencyCode?.length
                              ? newCurrencyCode
                              : null,
                          });
                        }
                      }}
                      enableEmptySelection
                    />
                  ),
                },
              ]
            : []),
          ...(hasFilterByCurrencyCodeMultiFeature
            ? [
                {
                  filterId: 'currencyCodes' as FilterToolbarItemId,
                  labelContentId: ContentId.Currency,
                  filterQueryKeys: ['currencyCodes'] as FilterToolbarItemId[],
                  filterItem: (
                    <CurrencyFilterMultiSelector
                      values={tempQuery.currencyCodes ?? []}
                      triggerProps={{ style: { width: '100%' } }}
                      placeholderText={ContentId.All}
                      onChange={({ newValues: newCurrencyCodes }) => {
                        if (
                          !isEqual(tempQuery.currencyCodes, newCurrencyCodes)
                        ) {
                          setTempQuery({
                            ...tempQuery,
                            currencyCodes: newCurrencyCodes?.length
                              ? newCurrencyCodes
                              : null,
                          });
                        }
                      }}
                      enableEmptySelection
                    />
                  ),
                },
              ]
            : []),
        ],
      },
      ...tagsFilter,
      ...(internalNotesFilter ? [internalNotesFilter] : []),
    ];
  }, [
    tempQuery,
    purchaseInfoFilters.items,
    showConsignmentFilter,
    isConsignmentFilter,
    initialQuery.paymentDates,
    hasFilterByPaymentCurrencyCodeMultiFeature,
    eventFilter,
    deliveryTypeFilter,
    isHorizontalLayout,
    hasFilterByCurrencyCodeFeature,
    hasFilterByCurrencyCodeMultiFeature,
    tagsFilter,
    internalNotesFilter,
    setTempQuery,
  ]);
};
